import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { Select } from "@libs/rubick";
import { FILTER_VALUES } from "@/constants/filters";
import useAuthStore from "@/context/auth/useAuthStore";
import { useApiKeys } from "@/hooks/api-keys";
import { isAuthHttpError } from "@/utils/httpError";
export const ApiKeyFilter = ({ selectedApiKey, onChange, productCode: productCodeProp, includeInactive = false, customerIdOverride })=>{
    const { productCode: authProductCode } = useAuthStore();
    const productCode = productCodeProp || authProductCode;
    const { data: apiKeysData, isLoading, error } = useApiKeys(productCode || "", !!productCode, customerIdOverride);
    const selectableApiKeys = useMemo(()=>apiKeysData?.items?.filter((key)=>includeInactive || key.status === "Active") ?? [], [
        apiKeysData?.items,
        includeInactive
    ]);
    const handleChange = (apiKeyId)=>{
        onChange(apiKeyId);
    };
    if (!productCode) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            children: [
                /*#__PURE__*/ _jsx("label", {
                    htmlFor: "apiKey",
                    className: "block text-sm font-medium text-gray-700 mb-2",
                    children: "API Key"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "relative",
                    children: /*#__PURE__*/ _jsx(Select, {
                        id: "apiKey",
                        value: FILTER_VALUES.ALL,
                        disabled: true,
                        children: /*#__PURE__*/ _jsx("option", {
                            value: FILTER_VALUES.ALL,
                            children: "All API Keys"
                        })
                    })
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mt-1.5 text-xs text-yellow-600 pl-1",
                    children: "No product configured. Please log in to view API keys."
                })
            ]
        });
    }
    // Handle authentication errors gracefully
    if (error) {
        const isAuthError = isAuthHttpError(error);
        return /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            children: [
                /*#__PURE__*/ _jsx("label", {
                    htmlFor: "apiKey",
                    className: "block text-sm font-medium text-gray-700 mb-2",
                    children: "API Key"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "relative",
                    children: /*#__PURE__*/ _jsx(Select, {
                        id: "apiKey",
                        value: FILTER_VALUES.ALL,
                        disabled: true,
                        children: /*#__PURE__*/ _jsx("option", {
                            value: FILTER_VALUES.ALL,
                            children: "All API Keys"
                        })
                    })
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mt-1.5 text-xs text-red-500 pl-1",
                    children: isAuthError ? "Authentication required to load API keys" : "Unable to load API keys"
                })
            ]
        });
    }
    // Handle no active API keys case
    if (!isLoading && selectableApiKeys.length === 0) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            children: [
                /*#__PURE__*/ _jsx("label", {
                    htmlFor: "apiKey",
                    className: "block text-sm font-medium text-gray-700 mb-2",
                    children: "API Key"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "relative",
                    children: /*#__PURE__*/ _jsx(Select, {
                        id: "apiKey",
                        value: FILTER_VALUES.ALL,
                        disabled: true,
                        children: /*#__PURE__*/ _jsx("option", {
                            value: FILTER_VALUES.ALL,
                            children: "All API Keys"
                        })
                    })
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mt-1.5 text-xs text-yellow-600 pl-1",
                    children: "No API keys found. Create an API key first to filter usage by key."
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative",
        children: [
            /*#__PURE__*/ _jsx("label", {
                htmlFor: "apiKey",
                className: "block text-sm font-medium text-gray-700 mb-2",
                children: "API Key"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative",
                children: /*#__PURE__*/ _jsxs(Select, {
                    id: "apiKey",
                    value: selectedApiKey,
                    onChange: (e)=>handleChange(e.target.value),
                    disabled: isLoading,
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: FILTER_VALUES.ALL,
                            className: "py-2",
                            children: isLoading ? "Loading API Keys..." : "All API Keys"
                        }),
                        selectableApiKeys.map((apiKey)=>/*#__PURE__*/ _jsxs("option", {
                                value: apiKey.id,
                                className: "py-2",
                                children: [
                                    apiKey.name ? `${apiKey.name} (${apiKey.id.slice(0, 8)}...)` : apiKey.id,
                                    apiKey.status === "Active" ? "" : ` (${apiKey.status})`
                                ]
                            }, apiKey.id))
                    ]
                })
            }),
            selectableApiKeys.length > 0 && /*#__PURE__*/ _jsxs("p", {
                className: "mt-1.5 text-xs text-gray-500 pl-1",
                children: [
                    selectableApiKeys.length,
                    " API key",
                    selectableApiKeys.length !== 1 ? "s" : "",
                    " available"
                ]
            })
        ]
    });
};
